import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import useSWR from "swr";
import {
  FactoringStatus,
  statusToJapanese,
} from "components/FactoringStatus";
import {
  swrKey,
  factoringListFetcher,
} from "components/factoringListApi";
import { getJaDisplayName as getJaFactoringName } from "components/factoringUtil";
import { getJaDisplayName as getJaUserName } from "components/userUtil";

export default function FactoringList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data, error, isLoading } = useSWR(
    [swrKey, page, rowsPerPage],
    factoringListFetcher,
  );

  // Initialize page from URL query parameter
  useEffect(() => {
    const pageParam = searchParams.get("page");
    if (pageParam) {
      setPage(parseInt(pageParam, 10));
    }
  }, [searchParams]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    // Update URL query parameter
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Update URL query parameter
    searchParams.set("page", "0");
    setSearchParams(searchParams);
  };

  // @ts-ignore
  const handleImageClick = (imageUrl: string, entryId: string) => {
    // Navigate to image details page with page number
    navigate(`/factoring/${entryId}/images?returnPage=${page}`);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        申込一覧
      </Typography>

      {/* Factoring Entries Table */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="factoring entries table">
          <TableHead>
            <TableRow>
              <TableCell>{getJaFactoringName("status")}</TableCell>
              <TableCell>{getJaFactoringName("applicationNumber")}</TableCell>
              <TableCell>{getJaUserName("companyName")}</TableCell>
              <TableCell>{getJaFactoringName("images")}</TableCell>
              <TableCell align="center">詳細</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {error && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box sx={{ mt: 2, mb: 2, color: "error.main" }}>
                    <Typography variant="h6" color="error">
                      エラーが発生しました
                    </Typography>
                    <Typography variant="body2">
                      {error.message || "データの取得中に問題が発生しました。"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {data &&
              data.factoringList.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <FactoringStatus status={entry.status}>
                      {statusToJapanese[entry.status]}
                    </FactoringStatus>
                  </TableCell>
                  <TableCell>{entry.applicationNumber}</TableCell>
                  <TableCell>{data.userList.find((u) => u.id === entry.userId)?.companyName ?? ""}</TableCell>
                  <TableCell sx={{ width: 300, maxWidth: 400 }}>
                    <Link
                      to={`/factoring/${entry.id}/images?returnPage=${page}`}
                      style={{ textDecoration: "none" }}
                    >
                      <Button variant="contained" color="primary" size="small">
                        画像詳細
                      </Button>
                    </Link>
                  </TableCell>
                  <TableCell align="center">
                    <Link
                      to={`/factoring/${entry.id}/details?returnPage=${page}`}
                      style={{ textDecoration: "none" }}
                    >
                      <Button variant="contained" color="primary" size="small">
                        詳細
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10]}
          component="div"
          count={data?.totalCount ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="表示件数:"
        />
      </TableContainer>
    </Container>
  );
}
